import { ReactNode } from 'react';
import Glyph, { type GlyphName } from './Glyph';

export type BeatKind = 'hook' | 'try' | 'reflect' | 'apply';

const COPY: Record<BeatKind, { label: string; intent: string; glyph: GlyphName; ring: string }> = {
  hook: {
    label: 'Hook',
    intent: 'A question to get you thinking before you try.',
    glyph: 'hook',
    ring: 'border-ink-200',
  },
  try: {
    label: 'Try',
    intent: 'Practice the skill in the workspace.',
    glyph: 'hand',
    ring: 'border-ink-300',
  },
  reflect: {
    label: 'Reflect',
    intent: 'Notice what changed and why.',
    glyph: 'mirror',
    ring: 'border-ink-200',
  },
  apply: {
    label: 'Apply',
    intent: 'Take it to a new context.',
    glyph: 'frame',
    ring: 'border-ink-200',
  },
};

export function BeatLabel({ kind }: { kind: BeatKind }) {
  const c = COPY[kind];
  return (
    <div className="flex items-baseline gap-3 mb-3">
      <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-ink-500">
        {c.label}
      </span>
      <span className="text-xs text-ink-500">{c.intent}</span>
    </div>
  );
}

export default function Beat({
  kind,
  children,
}: {
  kind: BeatKind;
  number?: number;
  children: ReactNode;
}) {
  const c = COPY[kind];
  return (
    <section className={`bg-white border ${c.ring} rounded-2xl p-6 sm:p-7`}>
      <header className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 grid place-items-center flex-shrink-0">
          <Glyph name={c.glyph} size={28} />
        </span>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-ink-900">{c.label}</div>
          <div className="text-xs text-ink-500">{c.intent}</div>
        </div>
      </header>
      <div className="space-y-3 text-ink-700 leading-relaxed">{children}</div>
    </section>
  );
}
